import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputComponent } from '../base/input.component';
import { LIST_DATA_PROVIDER, IListDataProvider } from '../../core/interfaces';

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: '',
    styles: [],
})
export abstract class ListViewComponent<ModelType> extends InputComponent<ModelType> {
	@Input() from?: 'hardCoded' | 'dietmarsMdb';
	@Input() options?: string;
	@Input() tableName?: string;
	@Input() tableColumn?: string;
	@Input() displayColumn?: string;
	@Input() prefix?: string;
	@Input() acceptOtherValues = false;

	private mapped = false;

	data = new Array<ModelType>;
	mappedData = new Map<ModelType,string>;
	table = new Array<any>;

	listDataProvider: IListDataProvider = inject(LIST_DATA_PROVIDER);

	constructor() {
		super();
	}

	override ngOnInit(): void {
		super.ngOnInit();

		const opt = this.os.option(this.name);
		if (opt) {
			if (opt.from !== undefined) this.from = opt.from;
			if (opt.options !== undefined) this.options = opt.options;
			if (opt.tableName !== undefined) this.tableName = opt.tableName;
			if (opt.tableColumn !== undefined) this.tableColumn = opt.tableColumn;
			if (opt.displayColumn !== undefined) this.displayColumn = opt.displayColumn;
			if (opt.prefix !== undefined) this.prefix = opt.prefix;
			if (opt.acceptOtherValues !== undefined) this.acceptOtherValues = opt.acceptOtherValues;
		}

		if (this.options) {
			const strData = this.options.split('|');
			strData.forEach(e => this.data?.push(this.fromString(e)));
		}
		else if (this.tableName) {
			if (!this.from) this.from = 'hardCoded';

			switch (this.from) {
				case 'hardCoded':
					this.table = this.listDataProvider.getHardcoded(this.tableName);
					break;
				case 'dietmarsMdb':
					this.table = this.listDataProvider.getTable(this.tableName);
					break;
				default:
					alert("Unbekannte Tabelle " + this.from);
			}
			if (this.tableColumn && this.displayColumn) this.mapped = true;
			if (!Array.isArray(this.table)) return;
			this.table.forEach((e: any) => {
				const val = this.tableColumn ? e[this.tableColumn] : e;
				if (val === undefined || val === null) return;
				const txt = val.toString() as string;
				if (txt !== null) this.data?.push(this.fromString(txt.trim()));
				if (this.mapped) {
					const key = e[this.tableColumn!];
					const displayString = e[this.displayColumn!];
					this.mappedData.set(key, displayString);
				}
			});
		}

		// Nach dem Laden der Daten erneut validieren, um ungültige Werte zu markieren
		if (this.data.length > 0 && this.model) {
			this.validate();
		}
	}

	abstract isEmptyValue(val: ModelType|undefined): boolean;
	abstract fromString(val: string): ModelType;

	displayString(val: ModelType|undefined): string {
		if (!this.mapped) {
			const result = this.prefix ? this.prefix : '';
			return this.isEmptyValue(val) ? '' : result + val;
		}
		else {
			const ds = this.isEmptyValue(val) ? '' : this.mappedData.get(val!);
			return ds || '';
		}
	}

	protected override baseValidate(): boolean {
		if (!super.baseValidate()) return this.successOnDanger();

		return this.doBaseValidate(this.model);
	}

	protected doBaseValidate(valueToCheck: ModelType|undefined) {
		if (!this.nonEmpty && !valueToCheck) return true;
		if (this.nonEmpty && !valueToCheck && !this.touched) return true; // Nicht validieren bevor Feld angefasst wurde

		if (this.data.length > 0 && this.data.indexOf(valueToCheck!) == -1) {
			// Bei acceptOtherValues: keine Validierungsmeldung
			if (this.acceptOtherValues) return true;
			this.danger("Wert nicht in der Liste");
			return this.successOnDanger();
		}

		return true;
	}
}

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule],
    selector: 'wd-list',
    templateUrl: 'list-view.component.html',
    styleUrls: ['../styles/view.component.css', '../styles/view-only.component.css'],
})
export class TextListViewComponent extends ListViewComponent<string> {
	constructor() {
		super();
	}

	override isEmptyValue(val: string|undefined): boolean {
		return val === undefined || val === null || val.length === 0;
	}

	override fromString(val: string): string {
		return val;
	}
}

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule],
    selector: 'wd-number-list',
    templateUrl: 'list-view.component.html',
    styleUrls: ['../styles/view.component.css', '../styles/view-only.component.css'],
})
export class NumberListViewComponent extends ListViewComponent<number> {
	constructor() {
		super();
	}

	override isEmptyValue(val: number|undefined): boolean {
		return val === undefined || val === null || val !== undefined && Number.isNaN(val);
	}

	override fromString(val: string): number {
		return parseInt(val);
	}
}
